import React, { useState, useEffect } from 'react';
import { MapPin, Map, RotateCcw } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useTranslation } from '../lib/i18n';

// Leaflet marker icon düzeltmesi
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Maps = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapCenter, setMapCenter] = useState([39.9334, 32.8597]); // Türkiye merkezi
  const [mapZoom, setMapZoom] = useState(6);

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('maps.loginRequired'));
        return;
      }

      const { companiesAPI } = await import('../lib/api');
      const data = await companiesAPI.getAll();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(t('maps.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  // Konum bilgisi olan şirketleri filtrele
  const companiesWithLocation = companies.filter(company => 
    company.latitude && company.longitude && 
    (typeof company.latitude === 'number' || !isNaN(parseFloat(company.latitude))) &&
    (typeof company.longitude === 'number' || !isNaN(parseFloat(company.longitude)))
  );

  const resetMapView = () => {
    setMapCenter([39.9334, 32.8597]);
    setMapZoom(6);
  };

  const renderMapContent = () => {
    return (
      <>
        {companiesWithLocation.map((company) => (
          <Marker
            key={company.id}
            position={[parseFloat(company.latitude), parseFloat(company.longitude)]}
          >
            <Popup>
              <div className="p-2">
                <h3 className="font-semibold text-gray-800 dark:text-white">{company.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{company.address}</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  {company.latitude ? parseFloat(company.latitude).toFixed(4) : 'N/A'}, {company.longitude ? parseFloat(company.longitude).toFixed(4) : 'N/A'}
                </p>
                {company.phone && <p className="text-xs text-gray-500 dark:text-gray-400">📞 {company.phone}</p>}
                {company.email && <p className="text-xs text-gray-500 dark:text-gray-400">📧 {company.email}</p>}
              </div>
            </Popup>
          </Marker>
        ))}
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center text-red-600 dark:text-red-400">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (companiesWithLocation.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <div className="flex justify-center mb-4">
            <Map className="h-16 w-16 text-gray-300 dark:text-gray-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('maps.noCompaniesWithLocation')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('maps.noCompaniesWithLocationDesc')}
          </p>
          <button 
            onClick={() => window.location.href = '/companies'}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            <MapPin className="h-4 w-4 mr-2" />
            {t('maps.addLocationToCompanies')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10 p-8">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('maps.title')}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-lg">{t('maps.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Harita Bilgisi */}
      <div className="mb-8">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <span className="font-medium text-blue-800 dark:text-blue-200">
              {t('maps.standardMap')}
            </span>
          </div>
          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
            {t('maps.standardMapDesc')}
          </p>
        </div>
      </div>

      {/* Harita Kontrolleri */}
      <div className="mb-6 flex items-center space-x-3">
        <button
          onClick={resetMapView}
          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          {t('maps.defaultView')}
        </button>
        
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {companiesWithLocation.length} {t('maps.companyLocationsShown')}
        </div>
      </div>

      {/* Harita */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <div className="h-[600px] w-full">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            
            {renderMapContent()}
          </MapContainer>
        </div>
      </div>

      {/* Şirket Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('maps.companyLocations')}</h3>
        </div>
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companiesWithLocation.map((company) => (
              <div key={company.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 dark:text-white">{company.name}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{company.address}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      {company.latitude ? parseFloat(company.latitude).toFixed(4) : 'N/A'}, {company.longitude ? parseFloat(company.longitude).toFixed(4) : 'N/A'}
                    </p>
                    {company.phone && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">📞 {company.phone}</p>}
                    {company.email && <p className="text-xs text-gray-500 dark:text-gray-400">📧 {company.email}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Maps;
